import { useMemo, useRef, useState } from "react";
import { Dialog, Section } from "@hybrid-iptv/ui";
import { useProfiles } from "../settings/profileStore.js";
import { useSubscriptions } from "../social/subscriptions.js";
import { useYoutubeConnection } from "../social/youtubeAccount.js";
import { useCreators, youtubeWatchUrl } from "../social/youtubeApi.js";
import { matchSubscriptions, searchableTerm, useYoutubeSearch } from "../social/youtubeSearch.js";
import { TrailerModal } from "../library/TrailerModal.js";
import { youtubeEmbedUrl } from "../library/vodHooks.js";
import { normalize } from "./useUniversalSearch.js";

/**
 * "From YouTube" in Search (docs/social-connect-youtube.md, Phase 2e), at the end of the All view.
 *
 *  - Your subscriptions that match show as you type, with their latest video (or LIVE).
 *  - One row, "Search all of YouTube for ...", runs the search on OK; Done on the keyboard and
 *    Enter run it too (the page's form). Results come from our server, never per keystroke.
 *  - A Kids profile sees none of it.
 *
 * A video plays in the same window the Social page uses; one its owner will not let play
 * embedded says so and stays here.
 */

interface Props {
  /** What is in the box now. */
  query: string;
  /** The term the viewer submitted (Done, Enter, or the row below); null before any. */
  submitted: string | null;
  onSubmit: (term: string) => void;
}

interface Row {
  key: string;
  videoId: string;
  title: string;
  /** The channel and, where it matters, LIVE in words. */
  line: string;
  thumbnail: string;
  live: boolean;
  /** What the window and the unavailable message call it. */
  name: string;
}

export function YoutubeSearchSection({ query, submitted, onSubmit }: Props) {
  const profile = useProfiles((state) => state.profiles.find((p) => p.id === state.activeProfileId));
  const kids = profile?.isKids === true;
  const { connected } = useYoutubeConnection(profile?.id);
  const subscriptions = useSubscriptions(profile?.id, !kids && connected === true);

  const needle = normalize(query);
  const typed = searchableTerm(query);
  const matches = useMemo(
    // Only a connected profile's own subscriptions: a cache left over from before a disconnect is
    // never read.
    () => (kids || typed === null || connected !== true ? [] : matchSubscriptions(subscriptions.channels, query)),
    [kids, typed, connected, subscriptions.channels, query],
  );
  const matchIds = useMemo(() => matches.map((channel) => channel.id), [matches]);
  const creators = useCreators(matchIds);

  // Results belong to the term that was submitted: editing the box puts them away until the
  // viewer submits again, so a stale answer never sits under a different word.
  const submittedTerm = submitted !== null && normalize(submitted) === needle ? searchableTerm(submitted) : null;
  const search = useYoutubeSearch(kids ? null : submittedTerm);

  const [playing, setPlaying] = useState<{ videoId: string; name: string } | null>(null);
  const [cantPlay, setCantPlay] = useState<string | null>(null);
  const pressed = useRef<HTMLElement | null>(null);
  const giveFocusBack = () => {
    const row = pressed.current;
    requestAnimationFrame(() => {
      if (row?.isConnected) row.focus();
    });
  };
  const play = (videoId: string, name: string) => {
    pressed.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setPlaying({ videoId, name });
  };

  const subscriptionRows = useMemo<Row[]>(() => {
    const answers = new Map((creators.data?.channels ?? []).map((channel) => [channel.id, channel]));
    const rows: Row[] = [];
    for (const channel of matches) {
      const answer = answers.get(channel.id);
      const live = creators.data?.liveChecked === true && answer?.live != null;
      const video = (live ? answer?.live : null) ?? answer?.latest;
      if (!video) continue;
      rows.push({
        key: `sub:${channel.id}`,
        videoId: video.videoId,
        title: video.title,
        line: `${live ? "LIVE · " : ""}${channel.title} · your subscription`,
        thumbnail: video.thumbnail,
        live,
        name: channel.title,
      });
    }
    return rows;
  }, [matches, creators.data]);

  const resultRows = useMemo<Row[]>(() => {
    const taken = new Set(subscriptionRows.map((row) => row.videoId));
    return (search.data?.results ?? [])
      .filter((result) => !taken.has(result.videoId))
      .map((result) => ({
        key: `yt:${result.videoId}`,
        videoId: result.videoId,
        title: result.title,
        line: `${result.live ? "LIVE · " : ""}${result.channelTitle}`,
        thumbnail: result.thumbnail,
        live: result.live,
        name: result.channelTitle,
      }));
  }, [search.data, subscriptionRows]);

  if (kids || typed === null) return null;

  const searched = submittedTerm !== null;
  const rows = [...subscriptionRows, ...resultRows];
  const nothingElse = searched && !search.isLoading && !search.isError && !search.data?.quotaLimited && resultRows.length === 0;

  return (
    <Section title="From YouTube">
      <div className="search-results">
        {rows.map((row) => (
          <div className="search-result" key={row.key}>
            <button
              type="button"
              className="search-result-main"
              onClick={() => play(row.videoId, row.name)}
              aria-label={`${row.live ? "Live now: " : ""}${row.title}, ${row.name}`}
            >
              <img className="search-result-art search-result-art--wide" src={row.thumbnail} alt="" loading="lazy" decoding="async" />
              <span className="search-result-text">
                <span className="search-result-title">{row.title}</span>
                <span className="search-result-subtitle">{row.line}</span>
              </span>
            </button>
          </div>
        ))}
        {!searched && (
          <div className="search-result">
            <button type="button" className="search-result-main" onClick={() => onSubmit(typed)}>
              <span className="search-result-art search-result-art--placeholder" aria-hidden="true">
                ▶
              </span>
              <span className="search-result-text">
                <span className="search-result-title">Search all of YouTube</span>
                <span className="search-result-subtitle">for “{typed}”</span>
              </span>
            </button>
          </div>
        )}
      </div>
      {searched && search.isLoading && <p className="search-note">Searching YouTube…</p>}
      {searched && search.isError && <p className="search-note">Couldn&apos;t reach YouTube just now. Try again in a few minutes.</p>}
      {searched && search.data?.quotaLimited && (
        <p className="search-note">YouTube search is resting for today. Your subscriptions above still work.</p>
      )}
      {nothingElse && rows.length === 0 && <p className="search-note">Nothing found on YouTube for “{submittedTerm}”.</p>}
      {playing && (
        <TrailerModal
          embedUrl={youtubeEmbedUrl(youtubeWatchUrl(playing.videoId), { autoplay: true })!}
          label={`${playing.name} video`}
          onClose={() => {
            setPlaying(null);
            giveFocusBack();
          }}
          onUnavailable={() => {
            setPlaying(null);
            setCantPlay(playing.name);
          }}
        />
      )}
      <Dialog
        open={cantPlay !== null}
        title="Can't play here"
        onClose={() => {
          setCantPlay(null);
          giveFocusBack();
        }}
      >
        This video&apos;s owner only lets it play on YouTube, so it can&apos;t play in the app.
      </Dialog>
    </Section>
  );
}
